import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as d3 from "d3";
import { fetchAPI } from "../../config/api";
import { 
  getHeatColor,
  filterEvents as filterEventsUtil,
  computeAggregates as computeAggregatesUtil,
  formatAvg as formatAvgUtil,
  normalizeZone
} from "../../utils/sprayChartUtils";

const WIDTH = 800;
const HEIGHT = 500;

export const SprayChartReport = () => {
  const [searchParams] = useSearchParams();
  const svgRef = useRef();
  const [ready, setReady] = useState(false);
  const [playerData, setPlayerData] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  const [error, setError] = useState("");

  const injectedConfig = window.__SPRAY_CHART_CONFIG__ || null;
  const playerId = injectedConfig?.player_id || searchParams.get("player_id");
  const year = parseInt(String(injectedConfig?.year || searchParams.get("year") || "2025"), 10);
  const division = parseInt(String(injectedConfig?.division || searchParams.get("division") || "3"), 10);
  const handFilter = injectedConfig?.hand_filter || searchParams.get("hand_filter") || "all";
  const selectedZonesParam = injectedConfig?.selected_zones || searchParams.get("selected_zones");
  
  const selectedZones = useMemo(
    () => (selectedZonesParam ? selectedZonesParam.split(",") : []),
    [selectedZonesParam]
  );

  useEffect(() => {
    if (!playerId) return;

    const fetchData = async () => {
      setReady(false);
      setError("");
      try {
        const data = await fetchAPI(
          `/spraychart_data/${playerId}?year=${year}&division=${division}`
        );

        if (!data) {
          setError("No spray chart data returned.");
          setReady(true);
          return;
        }
        
        const bats = data.bats || "-";
        const team = data.team_name || "-";
        const playerInfo = `${bats ? bats.substring(0, 1).toUpperCase() : "-"} | ${year} | ${team}`;
        
        setPlayerData({
          player_name: data.player_name || "-",
          playerInfo,
        });

        const events = Array.isArray(data.events) ? data.events : [];
        const filteredEvents = filterEvents(events, handFilter, selectedZones);
        const agg = computeAggregates(filteredEvents.length ? filteredEvents : events);
        setAggregates(agg);
      } catch (err) {
        console.error("Error fetching data:", err);
        setAggregates(null);
        setPlayerData(null);
        setError(err?.message || "Failed to load spray chart data.");
        setReady(true);
      }
    };

    fetchData();
  }, [playerId, year, division, handFilter, selectedZones]);

  // Render D3 chart
  useEffect(() => {
    if (!svgRef.current || !aggregates) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);

    const { outfieldZones: outfieldZoneData, infieldZones: infieldZoneData, stats } = aggregates;

    const margin = { top: 100, right: 20, bottom: 60, left: 20 };
    const innerWidth = WIDTH - margin.left - margin.right;
    const innerHeight = HEIGHT - margin.top - margin.bottom;

    // Background
    svg.append("rect")
      .attr("width", WIDTH)
      .attr("height", HEIGHT)
      .attr("fill", "#F5FBFF");

    // Header
    const header = svg.append("g").attr("transform", "translate(20, 20)");
    
    header.append("text")
      .attr("x", 0)
      .attr("y", 24)
      .attr("font-size", "24px")
      .attr("font-weight", "bold")
      .attr("fill", "#1a1a1a")
      .text(playerData?.player_name || "Player");

    header.append("text")
      .attr("x", 0)
      .attr("y", 48)
      .attr("font-size", "14px")
      .attr("fill", "#666")
      .text(playerData?.playerInfo || "");

    const filterText = getFilterText(handFilter, selectedZones);
    header.append("text")
      .attr("x", 0)
      .attr("y", 68)
      .attr("font-size", "12px")
      .attr("fill", "#888")
      .text(filterText);

    // Field
    const field = svg.append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const centerX = innerWidth / 2;
    const centerY = innerHeight * 0.95;
    const arcRadius = Math.min(innerWidth * 0.42, innerHeight * 0.9);
    const infieldRadius = arcRadius * 0.52;

    drawInfield(field, { centerX, centerY, infieldRadius, infieldZoneData, selectedZones });
    drawOutfield(field, { centerX, centerY, arcRadius, infieldRadius, outfieldZoneData, selectedZones });

    // Stats
    const statsGroup = svg.append("g")
      .attr("transform", `translate(${WIDTH - 200}, 25)`);

    const statsData = [
      { label: "AVG", value: formatAvg(stats.battingAvg) },
      { label: "OBP", value: formatAvg(stats.onBasePercentage) },
      { label: "wOBA", value: formatAvg(stats.wOBA) },
      { label: "PA", value: stats.PA },
    ];

    statsData.forEach((stat, i) => {
      statsGroup.append("text")
        .attr("x", i * 45)
        .attr("y", 0)
        .attr("font-size", "10px")
        .attr("fill", "#666")
        .attr("text-anchor", "middle")
        .text(stat.label);
      
      statsGroup.append("text")
        .attr("x", i * 45)
        .attr("y", 18)
        .attr("font-size", "14px")
        .attr("font-weight", "bold")
        .attr("fill", "#1a1a1a")
        .attr("text-anchor", "middle")
        .text(stat.value);
    });

    // Watermark
    svg.append("text")
      .attr("x", WIDTH - 10)
      .attr("y", HEIGHT - 10)
      .attr("font-size", "10px")
      .attr("fill", "#aaa")
      .attr("text-anchor", "end")
      .text("d3-dashboard.com");

    setReady(true);
  }, [aggregates, playerData, selectedZones, handFilter]);

  if (!playerId) {
    return <div>Missing player_id parameter</div>;
  }

  if (error) {
    return (
      <div
        data-export-ready="true"
        data-chart-container
        style={{
          width: `${WIDTH}px`,
          height: `${HEIGHT}px`,
          backgroundColor: "#F5FBFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#374151",
          padding: "24px",
          textAlign: "center",
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div 
      data-export-ready={ready ? "true" : "false"}
      data-chart-container
      style={{ 
        width: `${WIDTH}px`, 
        height: `${HEIGHT}px`,
        backgroundColor: "#F5FBFF",
      }}
    >
      <svg
        ref={svgRef}
        width={WIDTH}
        height={HEIGHT}
        style={{ display: "block" }}
      />
    </div>
  );
};

const filterEvents = (events, handFilter, selectedZones) => {
  const handFilterObj = {
    L: handFilter === "all" || handFilter === "lhp",
    R: handFilter === "all" || handFilter === "rhp",
  };
  return filterEventsUtil(events, { handFilter: handFilterObj, selectedZones });
};

const computeAggregates = (events) => computeAggregatesUtil(events);

const getFilterText = (handFilter, selectedZones) => {
  const zoneText = selectedZones.length === 0 
    ? "All zones" 
    : `Zones: ${selectedZones.map(normalizeZone).join(", ")}`;
  const handText = handFilter === "all" ? "" : (handFilter === "rhp" ? "vs RHP" : "vs LHP");
  return handText ? `${zoneText} • ${handText}` : zoneText;
};

const formatAvg = formatAvgUtil;

const drawInfield = (field, { centerX, centerY, infieldRadius, infieldZoneData, selectedZones }) => {
  const pie = d3.pie()
    .startAngle(-0.785398)
    .endAngle(0.785398)
    .value(() => 1)
    .sort(null);

  const arc = d3.arc().innerRadius(0).outerRadius(infieldRadius);
  const slices = pie([1, 1, 1, 1, 1]);

  slices.forEach((slice, i) => {
    const zone = infieldZoneData[i];
    const percentage = zone?.percentage || 0;
    const zoneId = zone?.id || `infield-${i}`;
    const isSelected = selectedZones.includes(zoneId);
    const isDimmed = selectedZones.length > 0 && !isSelected;
    const fillColor = getHeatColor(percentage, 'infield');

    field.append("path")
      .attr("d", arc(slice))
      .attr("transform", `translate(${centerX},${centerY})`)
      .attr("fill", isDimmed ? "#E5E7EB" : fillColor)
      .attr("stroke", "#333")
      .attr("stroke-width", isSelected ? 2 : 0.5)
      .attr("opacity", isDimmed ? 0.15 : 1);

    if (percentage > 0 && !isDimmed) {
      const angle = (slice.startAngle + slice.endAngle) / 2;
      const textR = infieldRadius * 0.6;
      field.append("text")
        .attr("x", centerX + Math.sin(angle) * textR)
        .attr("y", centerY - Math.cos(angle) * textR)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-weight", "bold")
        .attr("font-size", "14px")
        .attr("fill", "#333")
        .text(`${percentage}%`);
    }
  });
};

// Draw outfield zones with HR circles
const drawOutfield = (field, { centerX, centerY, arcRadius, infieldRadius, outfieldZoneData, selectedZones }) => {
  const pie = d3.pie()
    .startAngle(-0.785398)
    .endAngle(0.785398)
    .value(() => 1)
    .sort(null);

  const arc = d3.arc().innerRadius(infieldRadius).outerRadius(arcRadius);
  const slices = pie([1, 1, 1]);

  slices.forEach((slice, i) => {
    const zone = outfieldZoneData[i];
    const percentage = zone?.percentage || 0;
    const hrCount = zone?.hrCount || 0;
    const hrPercentage = zone?.hrPercentage || 0;
    const zoneId = zone?.id || `outfield-${i}`;
    const hrZoneId = `${zoneId}-hr`;

    const isSelected = selectedZones.includes(zoneId);
    const isDimmed = selectedZones.length > 0 && !isSelected;
    const isHRSelected = selectedZones.includes(hrZoneId);
    const isHRDimmed = selectedZones.length > 0 && !isHRSelected;
    const fillColor = getHeatColor(percentage, 'outfield');

    // Main zone
    field.append("path")
      .attr("d", arc(slice))
      .attr("transform", `translate(${centerX},${centerY})`)
      .attr("fill", isDimmed ? "#E5E7EB" : fillColor)
      .attr("stroke", "#333")
      .attr("stroke-width", isSelected ? 2.5 : 1)
      .attr("opacity", isDimmed ? 0.15 : 0.95);

    const angle = (slice.startAngle + slice.endAngle) / 2;
    const textR = (infieldRadius + arcRadius) / 2;

    if (!isDimmed) {
      field.append("text")
        .attr("x", centerX + Math.sin(angle) * textR)
        .attr("y", centerY - Math.cos(angle) * textR)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-weight", "bold")
        .attr("font-size", "24px")
        .attr("fill", "#333")
        .text(`${percentage}%`);
    }

    // HR circle
    const hrRadius = arcRadius * 1.15;
    const hrX = centerX + Math.sin(angle) * hrRadius;
    const hrY = centerY - Math.cos(angle) * hrRadius;
    const circleR = 22;

    field.append("circle")
      .attr("cx", hrX)
      .attr("cy", hrY)
      .attr("r", circleR)
      .attr("fill", isHRDimmed ? "#E5E7EB" : "#E1F5FE")
      .attr("stroke", isHRSelected ? "#1565C0" : "#0D47A1")
      .attr("stroke-width", isHRSelected ? 2.5 : 1.5)
      .attr("opacity", isHRDimmed ? 0.15 : 1);

    if (!isHRDimmed) {
      field.append("text")
        .attr("x", hrX)
        .attr("y", hrY - 8)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-size", "10px")
        .attr("font-weight", "500")
        .attr("fill", "#0D47A1")
        .attr("opacity", 0.8)
        .text(`${hrPercentage}%`);

      field.append("text")
        .attr("x", hrX)
        .attr("y", hrY + 8)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-weight", "bold")
        .attr("font-size", "14px")
        .attr("fill", "#0D47A1")
        .text(hrCount);
    }
  });
};
